import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import {
  setPosts,
  setPostIsLiked,
} from "../postSlice";

// Thunk to setup a real-time listener for likes by the current user
export const setupUserLikesListener = createAsyncThunk(
  "posts/setupUserLikesListener",
  async (userId, { dispatch, rejectWithValue }) => {
    try {
      if (!userId) throw new Error("User ID is required for likes listener");
      return await new Promise((resolve, reject) => {
        try {
          const q = query(
            collection(db, "likes"),
            where("userId", "==", userId),
          );
          const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
              try {
                snapshot.docChanges().forEach((change) => {
                  const data =
                    typeof change?.doc?.data === "function"
                      ? change.doc.data()
                      : {};
                  const postId = data?.postId || null;
                  if (!postId) return;
                  if (change.type === "removed") {
                    dispatch(setPostIsLiked({ postId, isLiked: false }));
                  } else {
                    // added or modified
                    dispatch(setPostIsLiked({ postId, isLiked: true }));
                  }
                });
              } catch (e) {
                if (process.env.NODE_ENV !== "production")
                  console.warn("[likesListener] change processing error", e);
              }
            },
            (error) => {
              if (process.env.NODE_ENV !== "production")
                console.error("[likesListener] error", error);
              // Keep previous state; listener might be re-established on next call
            },
          );
          resolve(unsubscribe);
        } catch (e) {
          reject(e);
        }
      });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

export const setupPostsListener = createAsyncThunk(
  "posts/setupListener",
  async (_, { dispatch, getState }) => {
    // Wrap in a promise that resolves after first snapshot to avoid empty-feed flicker
    return await new Promise((resolve, reject) => {
      try {
        // Query all posts, ordered by timestamp (no status filter)
        // Filtering for status happens client-side below
        const q = query(
          collection(db, "posts"),
          orderBy("timestamp", "desc"),
          limit(50),
        );
        let firstResolved = false;

        const unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            try {
              const postList = snapshot.docs.reduce((acc, docSnap) => {
                try {
                  const data = docSnap.data();

                  // Filter out deleted posts client-side
                  // Allow posts with status: 'active', no status field, or any status except 'deleted'
                  if (data.status === "deleted") {
                    return acc; // Skip deleted posts
                  }

                  const timestamp =
                    data.timestamp &&
                    typeof data.timestamp.toDate === "function"
                      ? data.timestamp.toDate()
                      : new Date();

                  const avatar = data.userPhotoURL;
                  const isValidAvatar =
                    avatar &&
                    avatar !== "null" &&
                    avatar !== "undefined" &&
                    typeof avatar === "string" &&
                    avatar.trim() !== "";

                  const username =
                    data.username ||
                    data.userName ||
                    data.displayName ||
                    "User";
                  const content =
                    typeof data.content === "string"
                      ? data.content
                      : typeof data.text === "string"
                        ? data.text
                        : typeof data.description === "string"
                          ? data.description
                          : "";

                  acc.push({
                    id: docSnap.id,
                    userId: data.userId || data.uid || null,
                    username,
                    userProfession:
                      data.userProfession || data.profession || "",
                    content,
                    timestamp,
                    actionTag: data.actionTag || "describe",
                    userPhotoURL: isValidAvatar ? avatar : null,
                    title: data.title || "",
                    imageUrl: data.imageUrl || "",
                    imageUrls: Array.isArray(data.imageUrls)
                      ? data.imageUrls
                      : [],
                    link: data.link || "",
                    issue: data.issue || null,
                    scale: data.scale || "",
                    location: data.location || "",
                    issueStatus: data.issueStatus || "",
                    shares: data.shares || 0,
                    commentCount: data.commentCount || 0,
                    topLevelCommentCount:
                      (data.topLevelCommentCount ?? data.commentCount) || 0,
                    likeCount: data.likeCount || 0,
                    projectId: data.projectId || null,
                    hasProject: !!data.hasProject,
                    status: data.status || "active", // Include status for reference
                    clientRequestId: data.clientRequestId || null,
                    // Shared post fields - required for reshared posts to display correctly
                    type: data.type || null,
                    sharedPostId: data.sharedPostId || null,
                    sharedPostData: data.sharedPostData || null,
                    // Link post fields - for consistent link preview rendering
                    linkPreviewData: data.linkPreviewData || null,
                    isLinkPost: data.isLinkPost || false,
                  });
                } catch (e) {
                  if (process.env.NODE_ENV !== "production") {
                    console.error(
                      `[PostSlice] error transforming post ${docSnap.id}:`,
                      e,
                    );
                  }
                }
                return acc;
              }, []);

              const prev =
                getState && typeof getState === "function"
                  ? getState().posts?.posts || []
                  : [];

              // Preserve temp posts (optimistic updates) while merging real posts
              const tempPosts = prev.filter(
                (p) => p.id && p.id.startsWith("temp-"),
              );

              // Only keep temp posts that don't have a corresponding real post yet
              const validTempPosts = tempPosts.filter((temp) => {
                // Check if there's a real post with the same requestId
                const hasRealVersion = postList.some(
                  (real) =>
                    temp.__tempRequestId &&
                    real.clientRequestId &&
                    temp.__tempRequestId === real.clientRequestId,
                );
                return !hasRealVersion;
              });

              // Merge temp posts with real posts, temp posts at the beginning
              const mergedPosts = [...validTempPosts, ...postList];

              // Persist a compact cache for instant hydration on next load
              try {
                const toCache = mergedPosts.slice(0, 50).map((p) => ({
                  ...p,
                  timestamp:
                    p?.timestamp instanceof Date
                      ? p.timestamp.toISOString()
                      : p?.timestamp || null,
                }));
                localStorage.setItem(
                  "feed_posts_cache",
                  JSON.stringify(toCache),
                );
              } catch {}

              // Check if the merged result is different from previous state
              const sameLength = prev.length === mergedPosts.length;
              let sameIds = sameLength;
              if (sameLength) {
                for (let i = 0; i < prev.length; i++) {
                  if (prev[i]?.id !== mergedPosts[i]?.id) {
                    sameIds = false;
                    break;
                  }
                }
              }

              if (!(sameLength && sameIds)) {
                dispatch(setPosts(mergedPosts));
              }

              if (!firstResolved) {
                firstResolved = true;
                resolve(unsubscribe);
              }
            } catch (e) {
              console.error("Error processing posts snapshot:", e);
              if (!firstResolved) reject(e);
            }
          },
          (error) => {
            console.error("Error in posts listener:", error);
            // Do not clear posts on transient errors; keep previous state
            if (!firstResolved) reject(error);
          },
        );
      } catch (e) {
        reject(e);
      }
    });
  },
);
